import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import merge from "lodash/merge";
import { DEFAULT_DCUI_CONTENT, DEFAULT_DCUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { setError } from "@redux/actions/error-actions";
import {
    setVerificationConfiguration,
    setVerificationConfigurationLoading,
    setVerificationConfigurationLoadingFailed
} from "@redux/actions/verification-configuration-actions";
import { getVerificationConfiguration } from "@requests/get-verification-configuration";
import { AxnError } from "@iddataweb/idw-common-ui/utils";
import { Logger } from "@utils/logger";
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from "react-router-dom";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { navigateToUtil } from "@utils/navigation-util";
import { useAuditLogger } from "./use-audit-logger";
import { AuditLogLevel } from "@enums/audit-log-level";
import { initializeVerificationStepConfig } from "@redux/actions/verification-step-config-actions";
import { setLanguageContent } from "@redux/actions/language-actions";
import { AuditLogTypes } from "@constants/audit-log-types";
import { ConfigurationResponse } from "@idw-types/data";

export const useInitializeApplication = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { writeAuditLog } = useAuditLogger();
    const queryParametersState = useSelector(getQueryParametersState);

    const handleInvalidQueryParameters = () => {
        writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_ATTEMPTED_BUT_NO_REQUEST_TOKEN_FOUND);
        const errorMessage = `[AppComponent] No token found to start the transaction at start url:  ${window.location}`;
        Logger.error(errorMessage);
        const err = new Error(errorMessage);
        const dataDogError = new Error('Missing transaction token');
        datadogRum.addError(err, { dataDogError });
        dispatch(setLanguageContent(DEFAULT_DCUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH]));
        dispatch(setError({
            name: "Token Error",
            message: 'Critical: Token was not provided',
            asi: queryParametersState.token,
            errorCode: "CONFIG_LOAD_ERROR",
            httpStatus: "400",
            errorURI: window.location.href
        }));
        dispatch(setVerificationConfigurationLoadingFailed());
        navigateToUtil("/error", navigate, location.pathname);
    };

    const initializeApplication = async () => {
        await writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_LAUNCHED);

        if (queryParametersState.token && queryParametersState.apiUrl) {
            const err = new AxnError();
            Logger.info(`[App] Retrieving config from url: ${queryParametersState.apiUrl}`);
            dispatch(setVerificationConfigurationLoading(true));

            try {
                const response = await getVerificationConfiguration(queryParametersState);
                Logger.info(`[App] Response obtained from the config API call: ${response}`);

                if (!response.ok) {
                    err.asi = queryParametersState.token;
                    err.errorCode = response.statusText;
                    err.errorURI = response.url;
                    err.httpStatus = `${response.status}`;
                    throw err;
                }

                const jsonResponse: ConfigurationResponse = await response.json();
                writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_CONFIG_RETRIEVED_SUCCESSFULLY);

                if (jsonResponse.properties?.theme) {
                    jsonResponse.properties.theme = merge({}, DEFAULT_DCUI_THEME, jsonResponse.properties.theme);
                }

                dispatch(setVerificationConfiguration(jsonResponse));
                dispatch(initializeVerificationStepConfig(jsonResponse.stepConfig.steps));
            } catch (error) {
                console.error(error);
                console.dir(error);
                Logger.error('[App] Fetch error', error);
                dispatch(setLanguageContent(DEFAULT_DCUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH]));
                dispatch(setError({
                    name: "Configuration Loading Error",
                    message: error.message,
                    asi: queryParametersState.token,
                    errorCode: error.errorCode,
                    errorURI: error.errorURI,
                    httpStatus: error.status,
                }));
                dispatch(setVerificationConfigurationLoadingFailed());
                Logger.error(`[App] Configuration retrieval failed`, error);
                writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_CONFIG_RETRIEVAL_FAILED, AuditLogLevel.ERROR);
                const dataDogError = new Error(`[App] Configuration retrieval failed`);
                datadogRum.addError(dataDogError, {
                    error
                });
                navigateToUtil("/error", navigate, location.pathname);
            }
        } else {
            handleInvalidQueryParameters();
        }
    };

    useEffect(() => {
        if (queryParametersState.initialized) {
            initializeApplication();
        }
    }, [queryParametersState.initialized]);
};